import React from 'react';
import { ScaleIcon, HeartHandshakeIcon } from 'lucide-react';
import { Button, ButtonLink } from './Button';
import { GoogleRating } from './GoogleRating';
import { BrandSeal, Halo, RuleDot } from './Decor';
import { WHATSAPP_URL } from '../data/site';
import { SECTION_IDS, scrollToSection } from '../utils/scroll';
const HERO_IMAGE = 'https://cdn.magicpatterns.com/uploads/7TcS2WyNggtajebpqTxdBT/ChatGPT_Image_Jul_30,_2026,_02_05_41_PM.png';
function FloatingCard({
  icon,
  title,
  body,
  className = ''
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  className?: string;
}) {
  return <div className={`flex max-w-[15rem] items-start gap-3 rounded-2xl border border-lavender-200 bg-ivory/95 p-3.5 shadow-float backdrop-blur-[2px] ${className}`} data-id="element-89" data-mp="sai3509k0p57">
      <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-pill bg-lavender-50 text-plum-700" data-id="element-90" data-mp="3d1a8hk3o4bda">
        {icon}
      </span>
      <span className="leading-tight" data-id="element-91" data-mp="3814alm6t1smr">
        <span className="block text-[0.8rem] font-semibold text-plum-900" data-id="element-92" data-mp="3j8sdtwfflbnq">{title}</span>
        <span className="mt-1 block text-[0.72rem] leading-snug text-muted" data-id="element-93" data-mp="3o7lrppymr4of">{body}</span>
      </span>
    </div>;
}
export function Hero() {
  return <section id="top" className="relative overflow-hidden bg-cream" data-id="element-94" data-mp="399jnrenosh6n">
      {/* Geometría de marca, muy sutil */}
      <div aria-hidden="true" className="pointer-events-none absolute -right-40 -top-40 hidden h-[34rem] w-[34rem] rounded-full border border-lavender-200/70 lg:block" data-id="element-95" data-mp="3e7l5nw9dmagj" />
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 top-24 hidden h-[22rem] w-[22rem] rounded-full border border-lavender-200/50 lg:block" data-id="element-96" data-mp="35aasg5d00b8z" />
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 bottom-0 h-32 bg-gradient-to-b from-transparent to-lavender-50" data-id="element-97" data-mp="2vjpzaxg3fyxg" />

      <div className="relative mx-auto grid max-w-content items-center gap-9 px-5 pb-14 pt-9 sm:px-8 sm:pb-16 sm:pt-12 lg:grid-cols-[minmax(0,1.02fr)_minmax(0,0.98fr)] lg:gap-16 lg:pb-28 lg:pt-20" data-id="element-98" data-mp="2lla5gmpombk9">
        <div className="max-w-xl" data-id="element-99" data-mp="nat6xa8uyik6">
          <p className="eyebrow flex items-center gap-3 text-plum-600" data-id="element-100" data-mp="n9qj5b6u0w8h">
            <RuleDot className="hidden sm:inline-flex" data-id="element-101" data-mp="ibgor2sxlznx" />
            Negocios en Florida · Consultoría &amp; Creación
          </p>

          <h1 className="mt-4 font-display text-[2.1rem] leading-[1.1] text-plum-900 sm:mt-5 sm:text-5xl lg:text-[3.5rem]" data-id="element-102" data-mp="deyks55fe5cn">
            Convierte tu idea en un negocio con estructura.
          </h1>

          <p className="mt-4 max-w-lg text-[1.0625rem] leading-relaxed text-muted sm:mt-5" data-id="element-103" data-mp="162m0p6vr47sy">
            Te acompañamos a formalizar tu empresa en Florida y construir una presencia
            profesional desde el comienzo.
          </p>

          <p className="mt-5 flex flex-wrap items-center gap-x-2.5 gap-y-1 sm:mt-6" data-id="element-104" data-mp="3lsp1j88sy9bs">
            {['Estructura', 'Identidad', 'Presencia digital'].map((word, index) => <React.Fragment key={word} data-id="element-105" data-mp="3htga4a18inje" data-mpi={index}>
                {index > 0 && <span aria-hidden="true" className="text-lavender-300" data-id="element-106" data-mp="1xavxukzeea7s">
                    ·
                  </span>}
                <span className="font-display text-[1.0625rem] text-plum-700 sm:text-lg" data-id="element-107" data-mp="269zivui4kzox">
                  {word}
                </span>
              </React.Fragment>)}
          </p>

          <div className="mt-7 flex flex-col gap-2.5 sm:mt-8 sm:flex-row sm:items-center sm:gap-3" data-id="element-108" data-mp="2b2k6geafugk0">
            <ButtonLink size="lg" analyticsId="hero_whatsapp" href={WHATSAPP_URL} data-id="element-109" data-mp="2kyvntiovxwlb">
              Quiero comenzar mi negocio
            </ButtonLink>
            <Button size="lg" variant="outline" analyticsId="cta-secondary-hero" onClick={() => scrollToSection(SECTION_IDS.proceso)} data-id="element-110" data-mp="2p1sbvox72kwn">
              Ver cómo funciona
            </Button>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-3 sm:mt-7" data-id="element-111" data-mp="zl11agdas3it">
            <GoogleRating analyticsId="google-reviews-hero" data-id="element-112" data-mp="4b46ts95i2ix" />
            <p className="text-[0.82rem] text-muted sm:text-sm" data-id="element-113" data-mp="49jd24ununm4">
              Atención personalizada · Servicios para negocios en Florida
            </p>
          </div>
        </div>

        {/* Composición de la fundadora */}
        <div className="relative mt-2 lg:mt-0" data-id="element-114" data-mp="9ah476xnerzr">
          <Halo className="-left-10 -top-10 h-[22rem] w-[22rem] lg:h-[30rem] lg:w-[30rem]" data-id="element-115" data-mp="9bs8d7v0a1re" />
          <div aria-hidden="true" className="absolute -bottom-5 -right-4 h-40 w-40 rounded-xl2 bg-lavender-100 lg:-bottom-8 lg:-right-8 lg:h-60 lg:w-60" data-id="element-116" data-mp="3hrbw5rcwqrz2" />

          <div className="relative" data-id="element-117" data-mp="3rgc0zre3chl6">
            <img src={HERO_IMAGE} alt="Anny, fundadora de Inkova, en su oficina en Florida" loading="eager" className="relative aspect-square w-full rounded-xl2 object-cover object-[50%_16%] shadow-lift ring-1 ring-ivory/80 sm:aspect-[4/5] lg:aspect-[3/4]" data-id="element-118" data-mp="3rheorqg4a3wv" />

            <FloatingCard icon={<ScaleIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden="true" data-id="element-120" data-mp="19hm8bzc5y8iy" />} title="Estructura + Digital" body="Todo en un solo lugar." className="absolute -left-3 top-6 hidden lg:flex lg:-left-10 lg:top-12" data-id="element-119" data-mp="19inky88tv32s" />

            <FloatingCard icon={<HeartHandshakeIcon className="h-4 w-4" strokeWidth={1.6} aria-hidden="true" data-id="element-122" data-mp="14k3v3wr45755" />} title="Acompañamiento real" body="Una persona detrás de cada proyecto." className="absolute -bottom-5 left-2 sm:left-6 lg:-right-8 lg:bottom-14 lg:left-auto" data-id="element-121" data-mp="14jay3nhyhw07" />

            <BrandSeal className="absolute -left-4 bottom-8 hidden h-24 w-24 lg:block" data-id="element-123" data-mp="1jajm5s67n9b8" />
          </div>
        </div>
      </div>
    </section>;
}